export type RideRecord = {
  timestamp?: string | null;
  elapsedSeconds?: number | null;
  distanceMeters?: number | null;
  speedMetersPerSecond?: number | null;
  powerWatts?: number | null;
  heartRateBpm?: number | null;
  cadenceRpm?: number | null;
  altitudeMeters?: number | null;
  latitudeDegrees?: number | null;
  longitudeDegrees?: number | null;
  temperatureCelsius?: number | null;
  coreTemperatureCelsius?: number | null;
  heatStrainIndex?: number | null;
  leftRightBalance?: number | null;
  torqueEffectivenessLeft?: number | null;
  torqueEffectivenessRight?: number | null;
  pedalSmoothnessLeft?: number | null;
  pedalSmoothnessRight?: number | null;
  vehicleCount?: number | null;
};

export type IntervalClassification =
  | "sprint"
  | "anaerobic"
  | "vo2max"
  | "threshold"
  | "sweet-spot"
  | "tempo"
  | "endurance"
  | "unclassified";

export type IntervalDetectionStatus = "available" | "no-intervals" | "insufficient-data" | "invalid-ftp";

export type DetectedWorkInterval = {
  id: string;
  startTimestamp: string | null;
  endTimestamp: string | null;
  startElapsedSeconds: number;
  endElapsedSeconds: number;
  durationSeconds: number;
  averagePower: number | null;
  normalizedPower: number | null;
  maxPower: number | null;
  averageHeartRate: number | null;
  maxHeartRate: number | null;
  averageCadence: number | null;
  workKilojoules: number | null;
  intensityFactor: number | null;
  percentOfFTP: number | null;
  classification: IntervalClassification;
  confidence: number;
  source: "power-smoothed";
  setId: string | null;
  repetitionNumber: number | null;
  totalRepetitionsInSet: number | null;
};

export type DetectedRecoveryInterval = {
  startTimestamp: string | null;
  endTimestamp: string | null;
  durationSeconds: number;
  averagePower: number | null;
  averageHeartRate: number | null;
  powerPercentOfFTP: number | null;
  associatedIntervalId: string;
};

export type ContinuousEffortSegment = DetectedWorkInterval;

export type IntervalDetectionConfiguration = {
  longGapMultiplier: number;
  maxMergeGapSeconds: number;
  maxShortRecoveryMergeSeconds: number;
  shortRecoveryMergeMinPowerRatio: number;
  startHysteresisSeconds: number;
  endHysteresisSeconds: number;
  maxPowerDipSeconds: number;
  maxCadenceInterruptionSeconds: number;
  rampLeadInSeconds: number;
  startThresholdRatioOffset: number;
  endThresholdRatioOffset: number;
  minSprintDurationSeconds: number;
  minAnaerobicDurationSeconds: number;
  minVo2maxDurationSeconds: number;
  minThresholdDurationSeconds: number;
  minSweetSpotDurationSeconds: number;
  minTempoDurationSeconds: number;
  minEnduranceDurationSeconds: number;
  minRecoveryDurationSeconds: number;
  minConfidence: number;
  maxSingleEnduranceCoverage: number;
};

export type IntervalDetectionResult = {
  status: IntervalDetectionStatus;
  detectedIntervals: DetectedWorkInterval[];
  continuousEffortSegments?: ContinuousEffortSegment[];
  detectedRecoveries: DetectedRecoveryInterval[];
  intervalCount: number;
  continuousSegmentCount?: number;
  totalWorkDurationSeconds: number;
  totalContinuousDurationSeconds?: number;
  totalRecoveryDurationSeconds: number;
  configurationUsed: IntervalDetectionConfiguration;
  warnings: string[];
};

export type WorkoutType =
  | "recovery"
  | "endurance"
  | "tempo"
  | "sweet-spot"
  | "threshold"
  | "vo2max"
  | "anaerobic"
  | "sprint"
  | "mixed"
  | "unclassified";

export type WorkoutClassificationStatus = "available" | "insufficient-data" | "invalid-ftp";

export type WorkoutHeartRateProgression = "rising" | "stable" | "falling" | "variable" | "unavailable";

export type WorkoutDetectedSet = {
  setId: string;
  classification: IntervalClassification;
  repetitionCount: number;
  averageDurationSeconds: number | null;
  averagePower: number | null;
  averagePercentOfFTP: number | null;
  averageRecoverySeconds: number | null;
  durationVariationPercent: number | null;
  powerVariationPercent: number | null;
  cadenceVariationPercent: number | null;
  heartRateProgression: WorkoutHeartRateProgression;
  confidence: number;
  isPrimarySet: boolean;
  members: string[];
};

export type WorkoutPowerFadeStatus = "improving" | "stable" | "fading" | "unavailable";

export type WorkoutClassificationResult = {
  status: WorkoutClassificationStatus;
  workoutType: WorkoutType;
  workoutName: string;
  summary: string;
  confidence: number;
  classificationCategory?: "structured-workout" | "free-ride";
  explanation?: string;
  confidenceFactors?: Array<{ label: string; score: number; reason: string }>;
  continuousEffortSummary?: string;
  primaryStructuredType: WorkoutType | null;
  secondaryStructuredTypes: WorkoutType[];
  structuredSetCount: number;
  primaryIntervalClassification: IntervalClassification | null;
  repetitionCount: number | null;
  plannedEquivalentDurationSeconds: number | null;
  averageWorkIntervalDurationSeconds: number | null;
  averageRecoveryDurationSeconds: number | null;
  averageWorkPower: number | null;
  averageWorkPercentOfFTP: number | null;
  averagePowerAcrossPrimarySet: number | null;
  powerVariationPercent: number | null;
  durationVariationPercent: number | null;
  intervalConsistencyPercent: number | null;
  firstRepAveragePower: number | null;
  finalRepAveragePower: number | null;
  powerFadePercent: number | null;
  powerTrendPercentPerRep: number | null;
  powerFadeStatus: WorkoutPowerFadeStatus;
  averageCadenceAcrossPrimarySet: number | null;
  cadenceVariationPercent: number | null;
  cadenceConsistencyPercent: number | null;
  firstRepAverageHeartRate: number | null;
  finalRepAverageHeartRate: number | null;
  heartRateChangeBpm: number | null;
  heartRateChangePercent: number | null;
  heartRateProgression: WorkoutHeartRateProgression;
  completedWorkDurationSeconds: number | null;
  supportingSegmentSummary: string;
  detectedSets: WorkoutDetectedSet[];
  warnings: string[];
};

export type RideAnalytics = {
  normalizedPower: number | null;
  intensityFactor: number | null;
  variabilityIndex: number | null;
  trainingStressScore: number | null;
  averagePower: number | null;
  workKilojoules: number | null;
  validPowerDurationSeconds: number | null;
  ftpUsed: number | null;
  powerZones: Array<{
    zoneName: string;
    seconds: number | null;
    percentageOfMovingTime: number | null;
  }>;
  aerobicDecoupling: {
    status: "available" | "insufficient-duration" | "insufficient-power" | "insufficient-heart-rate" | "insufficient-coverage" | "invalid-data";
    interpretation: string;
    decouplingPercent: number | null;
    firstHalfAveragePower: number | null;
    firstHalfAverageHeartRate: number | null;
    firstHalfEfficiencyFactor: number | null;
    secondHalfAveragePower: number | null;
    secondHalfAverageHeartRate: number | null;
    secondHalfEfficiencyFactor: number | null;
    validDurationSeconds: number;
    validSampleCount: number;
    coveragePercent: number | null;
    warmupExcludedSeconds: number;
  };
  intervalDetection: IntervalDetectionResult;
  workoutClassification: WorkoutClassificationResult;
};

export type Ride = {
  rideDate: Date | string | number | null;
  movingTime: number | null;
  elapsedTime: number | null;
  distance: number | null;
  averageSpeed: number | null;
  maximumSpeed: number | null;
  elevationGain: number | null;
  averageHeartRate: number | null;
  maxHeartRate: number | null;
  averageCadence: number | null;
  averagePower: number | null;
  maximumPower: number | null;
  calories: number | null;
  heatStrainIndexValues: number[];
  vehicleCount: number | null;
  records: RideRecord[];
  recordingIntervalSeconds: number | null;
  timingFallbackUsed?: boolean;
  timingNormalizationError?: string | null;
  analyticsValidationWarnings?: string[];
  rideAnalytics?: RideAnalytics | null;
  sourceFileName: string | null;
  sourceFileSizeBytes: number | null;
  rawData?: unknown;
  rawOneSecondRecords?: unknown[];
  powerDurationCurve?: Array<{
    durationSeconds: number;
    bestAveragePower: number | null;
    startIndex: number | null;
    endIndex: number | null;
  }>;
  normalizedPower?: number | null;
  if?: number | null;
  tss?: number | null;
  vi?: number | null;
  criticalPower?: number | null;
  wPrime?: number | null;
};

export function createEmptyRide(): Ride {
  return {
    rideDate: null,
    movingTime: null,
    elapsedTime: null,
    distance: null,
    averageSpeed: null,
    maximumSpeed: null,
    elevationGain: null,
    averageHeartRate: null,
    maxHeartRate: null,
    averageCadence: null,
    averagePower: null,
    maximumPower: null,
    calories: null,
    heatStrainIndexValues: [],
    vehicleCount: null,
    records: [],
    recordingIntervalSeconds: null,
    timingFallbackUsed: false,
    timingNormalizationError: null,
    analyticsValidationWarnings: [],
    rideAnalytics: null,
    sourceFileName: null,
    sourceFileSizeBytes: null
  };
}
